// =============================================================================
// FILE: packages/renderer/src/components/capture/LastMileTracer.tsx
// PURPOSE: "R2 Live Traceroute" — a professional, controllable telecom
//   infrastructure-journey visualization. Resolves the caller's city & ISP from
//   their IP, then animates a data packet leaving the user endpoint, travelling
//   through the building entrance, telecom equipment, network switch, manhole /
//   underground infrastructure, out to the service provider network — and back.
//   Includes playback controls (play/pause/restart), speed, label + flow
//   toggles, live telemetry, and a "Print Traceroute" professional output.
//   Engineering visualization style — no game-like effects.
// =============================================================================

import { useEffect, useRef, useState, type JSX, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  Radio, X, Loader2, MapPin, Play, Pause, RotateCcw, Printer,
} from 'lucide-react';
import { haptic, playBeep } from '../../lib/soundManager.js';
import { MiniGeoMap } from './MiniGeoMap.js';

interface GeoInfo { city: string; region: string; country: string; isp: string; ip: string; lat: number; lng: number; }

type Speed = 'slow' | 'normal' | 'fast';
const SPEED_MS: Record<Speed, number> = { slow: 11000, normal: 7000, fast: 4000 };

const BACKEND: string = (() => {
  const w = (typeof window !== 'undefined'
    ? (window as unknown as Record<string, unknown>).__BACKEND_URL__
    : undefined) as string | undefined;
  return w || ((import.meta.env.VITE_BACKEND_URL as string) ?? '');
})();

// ── Infrastructure journey definition ───────────────────────────────────────
interface Hop { id: string; label: string; sub: string; }
const HOPS: Hop[] = [
  { id: 'endpoint', label: 'User Endpoint',        sub: 'Building / workstation' },
  { id: 'entrance', label: 'Building Entrance',    sub: 'Entrance facility (BEF)' },
  { id: 'telecom',  label: 'Telecom Equipment',    sub: 'Telecom room (TR)' },
  { id: 'switch',   label: 'Network Switch',       sub: 'Access / core switch' },
  { id: 'manhole',  label: 'Manhole',              sub: 'Underground duct bank' },
  { id: 'provider', label: 'Service Provider',     sub: 'ISP / carrier network' },
];

// SVG geometry — horizontal journey
const VB_W = 1040, VB_H = 300;
const X0 = 96, X1 = 944;                     // spine start / end
const SPINE_Y = 214;
const NODE_XS = HOPS.map((_, i) => X0 + ((X1 - X0) * i) / (HOPS.length - 1));

async function resolveLocation(): Promise<GeoInfo> {
  try {
    let email = 'anonymous';
    try { email = JSON.parse(localStorage.getItem('rcd2.auth') ?? '{}')?.email || 'anonymous'; } catch { /* */ }
    const res = await fetch(`${BACKEND}/api/tracer/record`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, source: 'r2-live-traceroute' }),
      signal: AbortSignal.timeout(7000),
    });
    if (res.ok) {
      const d = await res.json() as { geo?: Record<string, unknown> };
      const g = d.geo ?? {};
      return {
        city: String(g.city || 'your city'), region: String(g.region || ''),
        country: String(g.country || ''), isp: String(g.isp || 'your provider'),
        ip: String(g.ip || '—'), lat: Number(g.lat) || 0, lng: Number(g.lng) || 0,
      };
    }
  } catch { /* fall through */ }
  try {
    const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(6000) });
    if (!res.ok) throw new Error('geo');
    const d = await res.json() as Record<string, unknown>;
    return {
      city: String(d.city || 'your city'), region: String(d.region || ''),
      country: String(d.country_name || ''), isp: String(d.org || 'your provider'),
      ip: String(d.ip || '—'), lat: Number(d.latitude) || 0, lng: Number(d.longitude) || 0,
    };
  } catch {
    return { city: 'your city', region: '', country: '', isp: 'your provider', ip: '—', lat: 0, lng: 0 };
  }
}

async function measureLatency(): Promise<number | null> {
  const samples: number[] = [];
  for (let i = 0; i < 3; i++) {
    const t0 = performance.now();
    try { await fetch(`${BACKEND}/api/`, { cache: 'no-store', signal: AbortSignal.timeout(4000) }); }
    catch { return samples.length ? Math.min(...samples) : null; }
    samples.push(performance.now() - t0);
  }
  return Math.min(...samples);
}

const MANHOLE_SPACING_M = 150;
function estimateRunMeters(seed: string): number {
  let h = 0;
  for (const ch of seed) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return 320 + (h % 2180);
}
function approxNode(lat: number, lng: number, runM: number, seed: string): { lat: number; lng: number } {
  let h = 0;
  for (const ch of seed) h = (h * 17 + ch.charCodeAt(0)) >>> 0;
  const bearing = ((h % 360) * Math.PI) / 180;
  const dLat = (runM / 111320) * Math.cos(bearing);
  const dLng = (runM / (111320 * Math.max(0.2, Math.cos((lat * Math.PI) / 180)))) * Math.sin(bearing);
  return { lat: lat + dLat, lng: lng + dLng };
}

// ── Line-art infrastructure icons (muted engineering style) ─────────────────
const STROKE = 'rgba(148,163,184,0.85)';
const FILL = 'rgba(15,23,38,0.9)';

function NodeIcon({ type, cx }: { type: string; cx: number }): JSX.Element {
  const y = 96; // icon vertical anchor (bottom near 160)
  const s = STROKE;
  switch (type) {
    case 'endpoint':
      return (
        <g strokeWidth={1.6} stroke={s} fill={FILL}>
          <rect x={cx - 22} y={y} width={44} height={60} rx={3} />
          <line x1={cx - 22} y1={y + 15} x2={cx + 22} y2={y + 15} />
          {[0, 1, 2].map((r) => [0, 1].map((c) => (
            <rect key={`${r}-${c}`} x={cx - 15 + c * 16} y={y + 22 + r * 12} width={10} height={7} rx={1} fill="rgba(94,234,212,0.25)" stroke="none" />
          )))}
          <circle cx={cx} cy={y - 12} r={7} fill="none" stroke={s} strokeWidth={1.6} />
          <path d={`M ${cx - 12} ${y - 1} a 12 8 0 0 1 24 0`} fill="none" stroke={s} strokeWidth={1.6} />
        </g>
      );
    case 'entrance':
      return (
        <g strokeWidth={1.6} stroke={s} fill={FILL}>
          <path d={`M ${cx - 24} ${y + 60} L ${cx - 24} ${y + 8} L ${cx} ${y - 8} L ${cx + 24} ${y + 8} L ${cx + 24} ${y + 60} Z`} />
          <rect x={cx - 8} y={y + 30} width={16} height={30} fill="rgba(94,234,212,0.18)" stroke={s} strokeWidth={1.2} />
          <line x1={cx - 24} y1={y + 18} x2={cx + 24} y2={y + 18} />
        </g>
      );
    case 'telecom':
      return (
        <g strokeWidth={1.6} stroke={s} fill={FILL}>
          <rect x={cx - 20} y={y - 6} width={40} height={72} rx={3} />
          {[0, 1, 2, 3, 4].map((r) => (
            <g key={r}>
              <rect x={cx - 15} y={y + r * 13} width={30} height={9} rx={1} fill="rgba(30,41,59,0.9)" stroke="rgba(148,163,184,0.5)" strokeWidth={0.8} />
              <circle cx={cx - 10} cy={y + 4.5 + r * 13} r={1.4} fill="rgba(94,234,212,0.8)" stroke="none" />
              <circle cx={cx - 5} cy={y + 4.5 + r * 13} r={1.4} fill="rgba(94,234,212,0.5)" stroke="none" />
            </g>
          ))}
        </g>
      );
    case 'switch':
      return (
        <g strokeWidth={1.6} stroke={s} fill={FILL}>
          <rect x={cx - 30} y={y + 20} width={60} height={26} rx={3} />
          {Array.from({ length: 8 }).map((_, i) => (
            <rect key={i} x={cx - 26 + i * 6.6} y={y + 34} width={4} height={7} rx={0.6} fill="rgba(94,234,212,0.35)" stroke="none" />
          ))}
          {[0, 1, 2, 3].map((i) => (
            <circle key={i} cx={cx - 20 + i * 8} cy={y + 27} r={1.6} fill="rgba(94,234,212,0.8)" stroke="none" />
          ))}
        </g>
      );
    case 'manhole':
      return (
        <g strokeWidth={1.6} stroke={s} fill={FILL}>
          <line x1={cx - 34} y1={y + 30} x2={cx + 34} y2={y + 30} stroke="rgba(148,163,184,0.35)" strokeDasharray="4 4" />
          <ellipse cx={cx} cy={y + 30} rx={22} ry={7} fill="rgba(30,41,59,0.9)" />
          <ellipse cx={cx} cy={y + 30} rx={13} ry={4} fill="none" stroke={s} strokeWidth={1.2} />
          <path d={`M ${cx - 22} ${y + 30} L ${cx - 16} ${y + 62} L ${cx + 16} ${y + 62} L ${cx + 22} ${y + 30}`} fill="rgba(15,23,38,0.9)" />
          <line x1={cx - 10} y1={y + 44} x2={cx + 10} y2={y + 44} stroke="rgba(94,234,212,0.4)" strokeWidth={1.2} />
        </g>
      );
    case 'provider':
      return (
        <g strokeWidth={1.6} stroke={s} fill={FILL}>
          <circle cx={cx} cy={y + 26} r={26} />
          <ellipse cx={cx} cy={y + 26} rx={26} ry={10} fill="none" stroke={s} strokeWidth={1} />
          <line x1={cx - 26} y1={y + 26} x2={cx + 26} y2={y + 26} stroke={s} strokeWidth={1} />
          <path d={`M ${cx} ${y} q -16 26 0 52`} fill="none" stroke={s} strokeWidth={1} />
          <path d={`M ${cx} ${y} q 16 26 0 52`} fill="none" stroke={s} strokeWidth={1} />
          <circle cx={cx} cy={y + 26} r={3} fill="rgba(94,234,212,0.8)" stroke="none" />
        </g>
      );
    default:
      return <g />;
  }
}

// Build a self-contained professional print document (new window).
function printTraceroute(geo: GeoInfo | null, latency: number | null, projectName: string): void {
  const runM = geo ? estimateRunMeters(geo.ip || geo.city) : 0;
  const runLabel = runM >= 1000 ? `${(runM / 1000).toFixed(2)} km` : `${Math.round(runM)} m`;
  const manholes = Math.max(1, Math.round(runM / MANHOLE_SPACING_M));
  const now = new Date();
  const dateStr = now.toLocaleString();
  const loc = geo ? `${geo.city}${geo.region ? ', ' + geo.region : ''}${geo.country ? ', ' + geo.country : ''}` : '—';

  // Static professional SVG of the route (labels on, no animation)
  const nodeSvg = HOPS.map((h, i) => {
    const x = NODE_XS[i]!;
    return `
      <circle cx="${x}" cy="${SPINE_Y}" r="7" fill="#0f766e" stroke="#0d9488" stroke-width="1.5"/>
      <line x1="${x}" y1="120" x2="${x}" y2="${SPINE_Y - 8}" stroke="#94a3b8" stroke-width="1"/>
      <rect x="${x - 26}" y="86" width="52" height="34" rx="3" fill="#f8fafc" stroke="#334155" stroke-width="1"/>
      <text x="${x}" y="106" text-anchor="middle" font-family="Georgia,serif" font-size="9" font-weight="700" fill="#0f172a">${i + 1}</text>
      <text x="${x}" y="${SPINE_Y + 26}" text-anchor="middle" font-family="Arial,sans-serif" font-size="10" font-weight="700" fill="#0f172a">${h.label}</text>
      <text x="${x}" y="${SPINE_Y + 40}" text-anchor="middle" font-family="Arial,sans-serif" font-size="8" fill="#64748b">${h.sub}</text>`;
  }).join('');

  const forwardArrows = NODE_XS.slice(0, -1).map((x, i) => {
    const mid = (x + NODE_XS[i + 1]!) / 2;
    return `<path d="M ${mid - 5} ${SPINE_Y - 5} L ${mid + 4} ${SPINE_Y} L ${mid - 5} ${SPINE_Y + 5} Z" fill="#0d9488"/>`;
  }).join('');

  const svg = `
  <svg viewBox="0 0 ${VB_W} 300" width="100%" xmlns="http://www.w3.org/2000/svg">
    <line x1="${X0}" y1="${SPINE_Y}" x2="${X1}" y2="${SPINE_Y}" stroke="#0d9488" stroke-width="2"/>
    ${forwardArrows}
    ${nodeSvg}
    <text x="${X0}" y="60" font-family="Arial,sans-serif" font-size="11" font-weight="700" fill="#0f172a" letter-spacing="1">FORWARD PATH  &#8594;  DATA LEAVES USER, TRAVELS TO SERVICE PROVIDER</text>
    <text x="${X0}" y="270" font-family="Arial,sans-serif" font-size="11" font-weight="700" fill="#b45309" letter-spacing="1">RETURN PATH  &#8592;  RESPONSE RETURNS TO USER ENDPOINT</text>
  </svg>`;

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
  <title>RCD² Engine — Traceroute · ${projectName}</title>
  <style>
    @page { size: A4 landscape; margin: 14mm; }
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; color: #0f172a; margin: 0; padding: 24px; background: #fff; }
    .hdr { display:flex; justify-content:space-between; align-items:flex-start; border-bottom: 2px solid #0f172a; padding-bottom: 10px; }
    .brand { font-size: 18px; font-weight: 800; letter-spacing: 0.5px; }
    .brand span { color:#0d9488; }
    .sub { font-size: 10px; color:#64748b; text-transform:uppercase; letter-spacing:2px; margin-top:2px; }
    .meta { text-align:right; font-size: 11px; color:#334155; line-height:1.6; }
    h2 { font-size: 12px; text-transform: uppercase; letter-spacing: 2px; color:#334155; margin: 20px 0 8px; border-bottom:1px solid #cbd5e1; padding-bottom:4px; }
    .diagram { border:1px solid #cbd5e1; padding:12px; margin-top:6px; }
    table { width:100%; border-collapse: collapse; font-size: 11px; margin-top:6px; }
    td, th { border:1px solid #cbd5e1; padding:6px 8px; text-align:left; }
    th { background:#f1f5f9; font-size:9px; text-transform:uppercase; letter-spacing:1px; color:#475569; }
    .grid { display:grid; grid-template-columns: repeat(3, 1fr); gap:12px; margin-top:6px; }
    .card { border:1px solid #cbd5e1; padding:10px 12px; }
    .card .k { font-size:9px; text-transform:uppercase; letter-spacing:1px; color:#64748b; }
    .card .v { font-size:20px; font-weight:800; color:#0f172a; margin-top:2px; }
    .legend { display:flex; gap:24px; font-size:10px; color:#475569; margin-top:8px; }
    .legend i { display:inline-block; width:22px; height:3px; vertical-align:middle; margin-right:6px; }
    .ftr { margin-top:22px; border-top:1px solid #cbd5e1; padding-top:8px; font-size:9px; color:#94a3b8; display:flex; justify-content:space-between; }
  </style></head><body>
    <div class="hdr">
      <div>
        <div class="brand">RCD<span>²</span> ENGINE</div>
        <div class="sub">BIM OPS Platform · Telecommunications Infrastructure</div>
      </div>
      <div class="meta">
        <div><strong>R2 Live Traceroute</strong></div>
        <div>Project: ${projectName}</div>
        <div>${dateStr}</div>
      </div>
    </div>

    <h2>Infrastructure Route — Last-Mile Journey</h2>
    <div class="diagram">${svg}</div>
    <div class="legend">
      <span><i style="background:#0d9488"></i>Forward path (user → service provider)</span>
      <span><i style="background:#b45309"></i>Return path (service provider → user)</span>
    </div>

    <h2>Network Elements &amp; Infrastructure Sequence</h2>
    <table>
      <thead><tr><th>#</th><th>Network Element</th><th>Description</th></tr></thead>
      <tbody>
        ${HOPS.map((h, i) => `<tr><td>${i + 1}</td><td><strong>${h.label}</strong></td><td>${h.sub}</td></tr>`).join('')}
      </tbody>
    </table>

    <h2>Connection Telemetry &amp; Estimates</h2>
    <div class="grid">
      <div class="card"><div class="k">Live round-trip latency</div><div class="v">${latency != null ? latency.toFixed(0) + ' ms' : '—'}</div></div>
      <div class="card"><div class="k">Est. last-mile run</div><div class="v">≈ ${geo ? runLabel : '—'}</div></div>
      <div class="card"><div class="k">Est. manholes @ ~${MANHOLE_SPACING_M} m</div><div class="v">~${geo ? manholes : '—'}</div></div>
    </div>

    <h2>Location</h2>
    <table>
      <tbody>
        <tr><th style="width:200px">Origin (user endpoint)</th><td>${loc}</td></tr>
        <tr><th>Service provider</th><td>${geo ? geo.isp : '—'}</td></tr>
        <tr><th>Coordinates</th><td>${geo && geo.lat ? geo.lat.toFixed(4) + ', ' + geo.lng.toFixed(4) : '—'}</td></tr>
        <tr><th>Public IP</th><td>${geo ? geo.ip : '—'}</td></tr>
      </tbody>
    </table>

    <div class="ftr">
      <span>RCD² Engine — Generated Traceroute Report. Estimates derived from IP geolocation &amp; standard duct-run spacing; not survey-grade.</span>
      <span>rcd2engine.com</span>
    </div>
    <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 300); };</script>
  </body></html>`;

  const w = window.open('', '_blank', 'width=1100,height=800');
  if (w) { w.document.open(); w.document.write(html); w.document.close(); }
  else {
    // Popup blocked — fall back to a printable hidden iframe.
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow?.document;
    if (doc) { doc.open(); doc.write(html); doc.close(); }
    setTimeout(() => document.body.removeChild(iframe), 2000);
  }
}

function TracerModal({ onClose, projectName }: { onClose: () => void; projectName: string }): JSX.Element {
  const [geo, setGeo] = useState<GeoInfo | null>(null);
  const [latency, setLatency] = useState<number | null>(null);

  // Animation controls
  const [t, setT] = useState(0);            // 0..2 (0..1 forward, 1..2 return)
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState<Speed>('normal');
  const [showLabels, setShowLabels] = useState(true);
  const [showFlow, setShowFlow] = useState(true);
  const rafRef = useRef<number | undefined>(undefined);
  const lastRef = useRef<number | undefined>(undefined);

  useEffect(() => { void resolveLocation().then(setGeo); }, []);
  useEffect(() => { void measureLatency().then(setLatency); }, []);

  useEffect(() => {
    if (!playing) { lastRef.current = undefined; return; }
    let active = true;
    const dur = SPEED_MS[speed];
    const step = (nowTs: number): void => {
      if (!active) return;
      if (lastRef.current == null) lastRef.current = nowTs;
      const dt = nowTs - lastRef.current;
      lastRef.current = nowTs;
      setT((prev) => {
        let next = prev + (dt / dur) * 2;
        if (next >= 2) next -= 2;
        return next;
      });
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { active = false; if (rafRef.current) cancelAnimationFrame(rafRef.current); lastRef.current = undefined; };
  }, [playing, speed]);

  const restart = (): void => { setT(0); setPlaying(true); };

  const runM = geo ? estimateRunMeters(geo.ip || geo.city) : 0;
  const runLabel = runM >= 1000 ? `${(runM / 1000).toFixed(2)} km` : `${Math.round(runM)} m`;
  const manholes = Math.max(1, Math.round(runM / MANHOLE_SPACING_M));
  const node = geo && geo.lat ? approxNode(geo.lat, geo.lng, runM, geo.ip || geo.city) : null;

  const forward = t < 1;
  const f = forward ? t : 2 - t;                       // 0..1 traversal fraction
  const packetX = X0 + (X1 - X0) * f;
  const activeSeg = Math.min(HOPS.length - 2, Math.floor(f * (HOPS.length - 1)));
  const flowColor = forward ? '#5eead4' : '#fbbf24';

  // trailing packets (only when data-flow is on)
  const trail = showFlow ? [0, 0.05, 0.1].map((d) => Math.max(0, f - d)) : [f];

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 10030, background: 'rgba(4,7,13,0.78)', backdropFilter: 'blur(6px)', display: 'grid', placeItems: 'center', padding: 20 }} onClick={onClose}>
      <div
        data-testid="last-mile-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(940px, 100%)', maxHeight: '92vh', overflowY: 'auto', borderRadius: 16, background: '#0b1220', border: '1px solid rgba(45,212,191,0.28)', boxShadow: '0 24px 70px rgba(0,0,0,0.6)', overflow: 'hidden' }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid rgba(45,212,191,0.16)' }}>
          <Radio style={{ width: 16, height: 16, color: '#5eead4' }} />
          <div style={{ lineHeight: 1.2 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#e6fffb' }}>R2 Live Traceroute</div>
            <div style={{ fontSize: 10, color: 'rgba(148,163,184,0.7)', letterSpacing: '0.14em', textTransform: 'uppercase' }}>Infrastructure journey · endpoint → provider → endpoint</div>
          </div>
          <button
            type="button" data-testid="traceroute-print-btn" data-nosound
            onClick={() => { haptic(8); printTraceroute(geo, latency, projectName); }}
            style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, background: 'rgba(45,212,191,0.1)', border: '1px solid rgba(45,212,191,0.4)', color: '#5eead4', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}
          >
            <Printer style={{ width: 13, height: 13 }} /> Print Traceroute
          </button>
          <button type="button" data-testid="last-mile-close" data-nosound onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'rgba(148,163,184,0.6)', cursor: 'pointer' }}><X style={{ width: 16, height: 16 }} /></button>
        </div>

        {/* Diagram */}
        <div style={{ padding: '14px 18px 6px', background: 'linear-gradient(180deg, rgba(15,23,42,0.6), transparent)' }}>
          <svg viewBox={`0 0 ${VB_W} ${VB_H}`} width="100%" style={{ display: 'block' }}>
            {/* baseline spine */}
            <line x1={X0} y1={SPINE_Y} x2={X1} y2={SPINE_Y} stroke="rgba(148,163,184,0.35)" strokeWidth={2} />

            {/* segment direction chevrons */}
            {NODE_XS.slice(0, -1).map((x, i) => {
              const mid = (x + NODE_XS[i + 1]!) / 2;
              const isActive = i === activeSeg;
              const col = isActive ? flowColor : 'rgba(148,163,184,0.4)';
              const dir = forward ? 1 : -1;
              return (
                <path key={i}
                  d={`M ${mid - 6 * dir} ${SPINE_Y - 5} L ${mid + 5 * dir} ${SPINE_Y} L ${mid - 6 * dir} ${SPINE_Y + 5} Z`}
                  fill={col} opacity={isActive ? 1 : 0.6} />
              );
            })}

            {/* animated flow overlay on spine */}
            {showFlow && (
              <line x1={X0} y1={SPINE_Y} x2={X1} y2={SPINE_Y}
                stroke={flowColor} strokeWidth={2} strokeLinecap="round"
                strokeDasharray="2 10" opacity={0.55}
                style={{ strokeDashoffset: -(t * 60), transition: 'stroke 0.4s' }} />
            )}

            {/* nodes */}
            {HOPS.map((h, i) => {
              const x = NODE_XS[i]!;
              const reached = f * (HOPS.length - 1) >= i - 0.15;
              return (
                <g key={h.id} data-testid={`hop-${h.id}`}>
                  <line x1={x} y1={128} x2={x} y2={SPINE_Y - 8} stroke="rgba(148,163,184,0.3)" strokeWidth={1} />
                  <NodeIcon type={h.id} cx={x} />
                  <circle cx={x} cy={SPINE_Y} r={6}
                    fill={reached ? flowColor : 'rgba(30,41,59,0.95)'}
                    stroke={reached ? flowColor : 'rgba(148,163,184,0.5)'} strokeWidth={1.5}
                    style={{ transition: 'fill 0.2s' }} />
                  {showLabels && (
                    <>
                      <text x={x} y={SPINE_Y + 28} textAnchor="middle" fontSize={11.5} fontWeight={700} fill="rgba(226,232,240,0.92)">{h.label}</text>
                      <text x={x} y={SPINE_Y + 44} textAnchor="middle" fontSize={9} fill="rgba(148,163,184,0.65)">{h.sub}</text>
                    </>
                  )}
                </g>
              );
            })}

            {/* travelling packet(s) */}
            {trail.map((tf, i) => {
              const px = X0 + (X1 - X0) * Math.max(0, Math.min(1, tf));
              return (
                <circle key={i} cx={px} cy={SPINE_Y} r={i === 0 ? 5 : 3}
                  fill={i === 0 ? '#ffffff' : flowColor}
                  opacity={i === 0 ? 1 : 0.5}
                  style={{ filter: `drop-shadow(0 0 5px ${flowColor})` }} />
              );
            })}

            {/* direction caption */}
            <text x={X0} y={SPINE_Y - 26} fontSize={10} fontWeight={700} letterSpacing="0.18em"
              fill={forward ? '#5eead4' : '#fbbf24'}>
              {forward ? 'FORWARD →  DATA LEAVING USER' : '← RETURN  RESPONSE TO USER'}
            </text>
          </svg>
        </div>

        {/* Controls */}
        <div data-testid="traceroute-controls" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '8px 18px 12px' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <CtrlBtn testid="tr-play-pause" onClick={() => setPlaying((p) => !p)} active={playing}>
              {playing ? <Pause style={{ width: 13, height: 13 }} /> : <Play style={{ width: 13, height: 13 }} />}
              {playing ? 'Pause' : 'Play'}
            </CtrlBtn>
            <CtrlBtn testid="tr-restart" onClick={restart}>
              <RotateCcw style={{ width: 13, height: 13 }} /> Restart
            </CtrlBtn>
          </div>

          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <span style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(148,163,184,0.6)', marginRight: 2 }}>Speed</span>
            {(['slow', 'normal', 'fast'] as Speed[]).map((s) => (
              <CtrlBtn key={s} testid={`tr-speed-${s}`} onClick={() => setSpeed(s)} active={speed === s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </CtrlBtn>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
            <CtrlBtn testid="tr-toggle-labels" onClick={() => setShowLabels((v) => !v)} active={showLabels}>Labels</CtrlBtn>
            <CtrlBtn testid="tr-toggle-flow" onClick={() => setShowFlow((v) => !v)} active={showFlow}>Data Flow</CtrlBtn>
          </div>
        </div>

        {/* Status + telemetry */}
        <div style={{ padding: '2px 18px 20px' }}>
          <div data-testid="last-mile-status" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 14px', borderRadius: 10, background: 'rgba(45,212,191,0.07)', border: '1px solid rgba(45,212,191,0.22)' }}>
            {geo ? <MapPin style={{ width: 15, height: 15, color: '#5eead4', flexShrink: 0 }} /> : <Loader2 className="animate-spin" style={{ width: 15, height: 15, color: '#5eead4' }} />}
            <span style={{ fontSize: 12.5, color: 'rgba(215,235,235,0.92)' }}>
              {geo
                ? <>Traceroute origin: <strong style={{ color: '#5eead4' }}>{geo.city}{geo.region ? `, ${geo.region}` : ''}</strong> via <strong style={{ color: '#fcd34d' }}>{geo.isp}</strong></>
                : 'Resolving your connection…'}
            </span>
          </div>

          <div data-testid="last-mile-telemetry" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 10 }}>
            {[
              { label: 'Live latency', value: latency != null ? `${latency.toFixed(0)} ms` : '…', hint: 'round-trip', tone: '#5eead4' },
              { label: 'Est. run to node', value: geo ? `≈ ${runLabel}` : '…', hint: 'last-mile fiber', tone: '#38bdf8' },
              { label: 'Est. manholes', value: geo ? `~${manholes}` : '…', hint: `@ ~${MANHOLE_SPACING_M} m spacing`, tone: '#fcd34d' },
            ].map((m) => (
              <div key={m.label} data-testid={`telemetry-${m.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                style={{ padding: '9px 10px', borderRadius: 10, background: 'rgba(15,23,42,0.7)', border: '1px solid rgba(94,234,212,0.14)' }}>
                <div style={{ fontSize: 8.5, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(148,163,184,0.7)' }}>{m.label}</div>
                <div style={{ fontSize: 17, fontWeight: 800, color: m.tone, lineHeight: 1.15, marginTop: 2 }}>{m.value}</div>
                <div style={{ fontSize: 8, color: 'rgba(148,163,184,0.5)' }}>{m.hint}</div>
              </div>
            ))}
          </div>

          {geo && geo.lat && node && (
            <div style={{ marginTop: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <MapPin style={{ width: 12, height: 12, color: '#fcd34d' }} />
                <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(148,163,184,0.7)' }}>You → Serving switch</span>
                <span style={{ marginLeft: 'auto', fontSize: 8.5, color: 'rgba(148,163,184,0.45)' }}>estimate · not survey-grade</span>
              </div>
              <MiniGeoMap a={{ lat: geo.lat, lng: geo.lng }} b={node} />
            </div>
          )}

          <p style={{ marginTop: 8, fontSize: 8.5, color: 'rgba(148,163,184,0.45)', lineHeight: 1.4 }}>
            Distance, manhole count &amp; node position are engineering estimates derived from IP geolocation and standard
            duct-run spacing — intended for planning, not as-built survey data.
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function CtrlBtn({ children, onClick, active, testid }:
  { children: ReactNode; onClick: () => void; active?: boolean; testid: string }): JSX.Element {
  return (
    <button
      type="button" data-testid={testid} data-nosound onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 8,
        fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s',
        background: active ? 'rgba(45,212,191,0.16)' : 'rgba(30,41,59,0.6)',
        border: `1px solid ${active ? 'rgba(45,212,191,0.5)' : 'rgba(148,163,184,0.25)'}`,
        color: active ? '#5eead4' : 'rgba(203,213,225,0.85)',
      }}
    >
      {children}
    </button>
  );
}

export function LastMileTracer({ projectName = 'RCD² Engine — Live Network' }: { projectName?: string } = {}): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid="trace-connection-btn"
        onClick={() => { haptic(10); playBeep(760); setOpen(true); }}
        className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-[11px] font-semibold tracking-wide transition-all"
        style={{ background: 'rgba(45,212,191,0.08)', border: '1px solid rgba(45,212,191,0.3)', color: 'rgba(94,234,212,0.9)' }}
        title="Trace your connection through the telecom infrastructure — endpoint to service provider and back"
      >
        <Radio style={{ width: 13, height: 13 }} /> R2 Live Traceroute
      </button>
      {open && <TracerModal onClose={() => setOpen(false)} projectName={projectName} />}
    </>
  );
}
